// Warehouse instrumentation for the owner's per-paper Citations + Mentions row
// (growth_2026_q3_citations_promo_surfaces): one impression per revealed row, one explicit
// click per link, both under a single source so the row has one denominator. `extra.link`
// splits the three links, which is why a fourth link belongs here rather than in a source of
// its own.
//
// wow_profile/views/work_strip_view.js builds the row. It is a Sprockets file with no module
// system, so it cannot call either tracking API; it publishes each count on the row as a data
// attribute and announces it with COUNT_EVENT. The resolution rule and the payloads live here.
//
// Two invariants this file exists to hold:
//
// An unknown count is an absent key, never a zero. The counts ride a JSONP batch fetcher that
// registers a success handler only and clears its queue before dispatch, so a declined, 404'd
// or dropped batch is spelled exactly like a slow one. `citations_count: 0` must mean the
// endpoint said zero.
//
// The row and both its items render `display: none` and are revealed by a positive count. An
// element with no box never reports isIntersecting, so a watcher registered before the reveal
// yields no impression ever. Registration is gated on a positive count — the same condition
// the legacy view reveals on, announced after it reveals.

import {
  ImpressionEntity,
  watchImpressions,
} from 'js/utils/impressions/watchImpressions';
import { trackClick } from 'js/utils/trackClick';
import { currentUser } from 'js/utils/user';

export const IMPRESSION_SOURCE = 'profile.work_strip.citations_mentions';

// work_strip_view.js hardcodes this string; a Sprockets file cannot import it.
export const COUNT_EVENT = 'profile-citations-row-count';

const ROW_SELECTOR = '.js-profile-citations-row';
const CITATIONS_ITEM = '.js-profile-work-strip-citations-count';
const MENTIONS_ITEM = '.js-profile-work-strip-view-mentions-container';
const VIEW_CITATIONS_ITEM = '.js-profile-work-strip-view-citations';

// profiles/autotest/batchFetchers' figure for this same fetcher: a 100ms debounce shared with
// the page's own work-strip callers plus a cross-origin round trip. Restated rather than
// imported — that module owns the autotest's fetch policy, not this surface's.
const DEADLINE_MS = 4000;

// Only these two publish a count. View citations is an action, not a statistic, so it has no
// `data-*-count` of its own and must stay out of every arrival and reveal check below.
type CountLink = 'citations' | 'mentions';

const COUNT_LINKS: readonly CountLink[] = ['citations', 'mentions'];

// `link`, not `kind`: clicks.extra.kind already means the descriptor tier on
// profile.citing_sources_card.view_click, a sibling source from this same experiment.
type Link = CountLink | 'view_citations';

type Counts = Partial<Record<CountLink, number>>;

// Read through the parsed helper: window.$current_user may be an unparsed JSON string.
const viewerIsPremium = (): boolean => currentUser?.premium ?? false;

const readCounts = (row: HTMLElement): Counts => {
  const counts: Counts = {};

  COUNT_LINKS.forEach((link) => {
    const raw = row.getAttribute(`data-${link}-count`)?.trim();
    if (raw == null || raw === '') return;

    const value = Number(raw);
    if (Number.isFinite(value)) counts[link] = value;
  });

  return counts;
};

const bothArrived = (counts: Counts): boolean =>
  COUNT_LINKS.every((link) => counts[link] !== undefined);

const anyArrived = (counts: Counts): boolean =>
  COUNT_LINKS.some((link) => counts[link] !== undefined);

const countExtra = (counts: Counts): Record<string, number> => {
  const extra: Record<string, number> = {};

  if (counts.citations !== undefined) extra.citations_count = counts.citations;
  if (counts.mentions !== undefined) extra.mentions_count = counts.mentions;

  return extra;
};

const workId = (row: HTMLElement): number | null => {
  const value = Number(row.getAttribute('data-work-id'));

  return Number.isInteger(value) && value > 0 ? value : null;
};

const resolved = new WeakSet<HTMLElement>();
const deadlines = new WeakMap<HTMLElement, number>();

// Returns whether a watcher was registered, which is what licenses the caller to stop
// listening: a row that reported nothing has been seen by nobody yet.
const emitImpression = (row: HTMLElement): boolean => {
  const counts = readCounts(row);
  const entityId = workId(row);

  // No positive count means the row stayed hidden. Nothing was seen, so nothing is reported.
  if (entityId == null) return false;
  if (!COUNT_LINKS.some((link) => (counts[link] ?? 0) > 0)) return false;

  watchImpressions(row, {
    source: IMPRESSION_SOURCE,
    entity_id: entityId,
    entity_type: ImpressionEntity.Work,
    extra: {
      ...countExtra(counts),
      is_premium: viewerIsPremium(),
    },
  });

  return true;
};

const resolve = (row: HTMLElement): void => {
  if (resolved.has(row)) return;

  const deadline = deadlines.get(row);
  if (deadline !== undefined) window.clearTimeout(deadline);
  deadlines.delete(row);

  // Expire the wait, not the row: a deadline that finds every count zero or absent has seen
  // nothing, and the later count that reveals the row is the one worth reporting. Clearing
  // the deadline lets that announcement re-arm.
  if (emitImpression(row)) resolved.add(row);
};

const handleRow = (row: HTMLElement): void => {
  if (resolved.has(row)) return;

  const counts = readCounts(row);

  if (bothArrived(counts)) {
    resolve(row);
    return;
  }

  // The deadline runs from the first count that arrives, not from the queue: a row whose
  // fetches both drop announces nothing, so it never resolves and never invents a zero.
  // Nothing landed means nothing to wait for yet — arming here, which the setup sweep would
  // otherwise do for every row on the page, would leave the second count only the remainder.
  if (!anyArrived(counts)) return;

  if (!deadlines.has(row)) {
    deadlines.set(
      row,
      window.setTimeout(() => resolve(row), DEADLINE_MS),
    );
  }
};

const onCount = (event: Event): void => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const row = target.closest<HTMLElement>(ROW_SELECTOR);
  if (row == null) return;

  handleRow(row);
};

const clickedLink = (target: Element): Link | null => {
  if (target.closest(CITATIONS_ITEM) != null) return 'citations';
  if (target.closest(MENTIONS_ITEM) != null) return 'mentions';
  if (target.closest(VIEW_CITATIONS_ITEM) != null) return 'view_citations';

  return null;
};

const onClick = (event: Event): void => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // Scoped to the third row: in control the Mentions item keeps its place on the premium row.
  const row = target.closest<HTMLElement>(ROW_SELECTOR);
  if (row == null) return;

  const link = clickedLink(target);
  if (link == null) return;

  // No displayed_count and no *_visible flags: both are derivable from the pair below, and a
  // derived key is a key that can disagree with its source.
  trackClick({
    source: IMPRESSION_SOURCE,
    entityId: workId(row),
    extra: {
      link,
      ...countExtra(readCounts(row)),
      is_premium: viewerIsPremium(),
    },
  });
};

// Delegated on document, so paginated cards need no second registration; watchImpressions
// dedupes globally on source:entity_type:entity_id, so a re-announced row cannot double-count.
export const setUpWorkStripCitationsMentionsAnalytics = (): void => {
  document.addEventListener(COUNT_EVENT, onCount);
  document.addEventListener('click', onClick);

  // The row is built by a RequireJS chain this pack does not order against, so a row whose
  // counts both landed first announced to nobody and would otherwise never be reported. The
  // usual state of a swept row is no counts at all — it is inserted before either fetcher
  // answers — which handleRow leaves for the announcement to arm.
  document.querySelectorAll<HTMLElement>(ROW_SELECTOR).forEach(handleRow);
};
